import { getAgencyById } from "rc-app/api/agencyActionsApi";
import { getAgentProfile, getAgentById } from "rc-app/api/agentActionsApi";
import { getCustomerProfile } from "rc-app/api/customerActionsApi";
import {
  getClientAdminByEmailId,
  getClientAdminById,
  getClientManagerByEmailId,
} from "rc-app/api/systemBusinessAdminActionApi";
import UserService from "rc-app/services/UserService";
import { toTitleCase } from "rc-app/utils";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDesktopMode } from "rc-app/hooks/useDesktopMode";
import DesktopModePrompt from "rc-app/components/DesktopModePrompt";

export const Profile = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { showPrompt, accept, dismiss } = useDesktopMode();
  const [loading, setLoading] = useState(true);
  const [clientManagers, setClientManagers] = useState<any>(null);
  const [clientAdmin, setClientAdmin] = useState<any>(null);
  const [agency, setAgency] = useState<any>(null);
  const [agent, setAgent] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [assignedAgent, setAssignedAgent] = useState<any>(null);
  const [assignedAdmin, setAssignedAdmin] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    if (UserService.hasRole("agency_business_admin")) {
      getClientManagerByEmailId(UserService.getUseremail())
        .then(async (res) => {
          if (res.detail) {
            toast(res.detail, { type: "error" });
          }
          setClientManagers(res.agency_business_admin);
          setAgency(await getAgencyById(res.agency_business_admin?.agency_id));
        })
        .catch((error) => {
          console.error("Error fetching client manager:", error);
        })
        .finally(() => setLoading(false));
    }

    if (UserService.hasRole("agency_system_admin")) {
      getClientAdminByEmailId(UserService.getUseremail())
        .then(async (res) => {
          if (res.detail) {
            toast(res.detail, { type: "error" });
          }
          setClientAdmin(res.agency_system_admin);
          setAgency(await getAgencyById(res.agency_system_admin?.agency_id));
        })
        .catch((error) => {
          console.error("Error fetching client admin:", error);
        })
        .finally(() => setLoading(false));
    }

    if (UserService.hasRole("agent")) {
      getAgentProfile(UserService.getUseremail())
        .then(async (res) => {
          if (res.detail) {
            toast(res.detail, { type: "error" });
          }
          setAgent(res.agent);
          // Admin Assigned: resolve the agent's assigned system admin. Backend
          // stores it as `assigned_asa_id` (a string); some views expose
          // `asa_id` as an array — prefer the string, fall back to the array.
          const asaId =
            res.agent?.assigned_asa_id ||
            (Array.isArray(res.agent?.asa_id)
              ? res.agent?.asa_id?.[0]
              : res.agent?.asa_id);
          if (asaId) {
            getClientAdminById(asaId)
              .then((r) => setAssignedAdmin(r?.agency_system_admin))
              .catch((e) =>
                console.error("Error resolving agent's system admin:", e),
              );
          }
          setAgency(await getAgencyById(res.agent?.agency["id"]));
        })
        .catch((error) => {
          console.error("Error fetching agent profile:", error);
        })
        .finally(() => setLoading(false));
    }

    if (UserService.hasRole("customer")) {
      getCustomerProfile(UserService.getUseremail())
        .then(async (res) => {
          if (res?.detail) {
            toast(res.detail, { type: "error" });
          }
          const cust = res?.customer;
          setCustomer(cust);
          // Developer Details: resolve the agency DIRECTLY from the customer
          // record (agent-independent) so it shows even when no agent is
          // assigned. Falls back to the assigned agent's agency below.
          const directAgencyId = cust?.agency_id;
          if (directAgencyId) {
            try {
              setAgency(await getAgencyById(directAgencyId));
            } catch (e) {
              console.error("Error resolving customer agency:", e);
            }
          }
          // Agent Assigned: resolve the assigned agent (if any). Also serves as
          // the agency fallback when the customer record has no agency_id.
          const agentId = cust?.agent_id?.[0];
          if (agentId) {
            getAgentById(agentId)
              .then((ares) => {
                setAssignedAgent(ares?.agent);
                if (!directAgencyId) {
                  const aId = ares?.agent?.agency?.id;
                  if (aId) getAgencyById(aId).then((a) => setAgency(a));
                }
              })
              .catch((e) => console.error("Error resolving customer agent:", e));
          }
        })
        .catch((error) => {
          console.error("Error fetching customer profile:", error);
        })
        .finally(() => setLoading(false));
    }
  }, []);
  // const [developerLogo, setDeveloperLogo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  if (showPrompt) {
    return (
      <DesktopModePrompt
        message="This page contains profile details that are best viewed in desktop mode. Switch to desktop view for the best experience."
        onAccept={accept}
        onGoBack={() => {
          dismiss();
          navigate(slug ? `/c/${slug}` : "/");
        }}
      />
    );
  }

  return (
    <>
      <div className="h-full p-3 sm:p-4 md:p-6 bg-[linear-gradient(180deg,#1C2C3A_0%,#1E4F52_100%)]">
        {/* Header */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <div className="flex justify-between items-center mb-2">
            {/* <button
              onClick={() => {
                navigate("/");
              }}
              className=" text-white flex items-center gap-2 hover:text-[#008564] transition-colors"
            >
              {`< My Profile`}
            </button> */}
            <div className="flex items-center gap-1 text-sm">
              <button
                type="button"
                onClick={() => navigate(slug ? `/c/${slug}` : "/")}
                className="text-[#889DB2] hover:underline"
              >
                Home
              </button>
              <span className="text-[#889DB2] mx-1">›</span>
              <span className="text-white font-medium">My Profile</span>
            </div>
          </div>
        </div>

        {/* Client Cards Grid */}
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6">
          {loading ? (
            <>
              <ClientCardSkeleton />
              <ClientCardSkeleton />
              <ClientCardSkeleton />
              {/* <div className="w-16 h-16 border-4 border-[#008564] border-t-transparent rounded-full animate-spin"></div> */}
            </>
          ) : (
            <div className="flex flex-col gap-2 sm:gap-3 md:gap-4">
              {/* Agent profile (Personal Information + Admin Assigned) renders
                  in the role-block section below, AFTER the shared Developer
                  Details card — so Developer Details shows first for agents. */}
              <div
                key="{clientId}"
                className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 px-5 py-5 md:px-7 md:py-6">
                  {/* Left: logo + identity */}
                  <div className="flex items-center gap-4 sm:gap-5 min-w-0 flex-1">
                    {agency?.logo && !hasError ? (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden shrink-0 relative bg-[#2E4051] p-2 flex items-center justify-center border border-[#374B5E]">
                        {isLoading && (
                          <div className="absolute inset-0 flex items-center justify-center text-[#F3CF08] text-xl sm:text-2xl font-bold">
                            {agency?.name?.slice(0, 2).toUpperCase() || ""}
                          </div>
                        )}
                        <img
                          src={agency?.logo}
                          alt=""
                          className={`w-full h-full object-contain ${
                            isLoading ? "opacity-0" : "opacity-100"
                          }`}
                          onLoad={() => setIsLoading(false)}
                          onError={() => {
                            setHasError(true);
                            setIsLoading(false);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-2xl flex items-center justify-center bg-[#2E4051] text-[#F3CF08] text-xl sm:text-2xl font-bold shadow-md shrink-0 border border-[#374B5E]">
                        {agency?.name?.slice(0, 2).toUpperCase() || "NA"}
                      </div>
                    )}

                    <div className="flex flex-col gap-1 min-w-0">
                      <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight truncate">
                        {agency?.name || "—"}
                      </h2>
                      {(agency?.incorporation_year || agency?.footprint) && (
                        <p className="text-xs sm:text-sm text-[#889DB2]">
                          {[agency?.incorporation_year ? `Established ${agency.incorporation_year}` : null, agency?.footprint].filter(Boolean).join("  ·  ")}
                        </p>
                      )}
                      {agency?.address && (
                        <div className="mt-1.5">
                          <p className="text-[10px] sm:text-xs text-[#889DB2] uppercase tracking-wide font-medium">
                            Registered Office
                          </p>
                          <p className="text-sm sm:text-base text-[#C5D0DB] mt-0.5 break-words">
                            {agency.address}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Sq Ft Built */}
                  <div className="shrink-0 border-t sm:border-t-0 sm:border-l border-[#374B5E] pt-4 sm:pt-0 sm:pl-6 md:pl-8">
                    <div className="flex flex-col sm:items-end">
                      <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-none">
                        {(() => {
                          const raw = agency?.client_sqft_built?.value;
                          const v = Number(raw);
                          if (raw == null || raw === "" || isNaN(v)) return "—";
                          if (v >= 1e6)
                            return (v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1) + "M";
                          if (v >= 1e3)
                            return (v / 1e3).toFixed(v % 1e3 === 0 ? 0 : 1) + "K";
                          return String(v);
                        })()}
                      </span>
                      <span className="text-[10px] sm:text-xs text-[#9DB5A6] uppercase tracking-wide mt-1.5">
                        Sq Ft Built
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {UserService.hasRole("agency_business_admin") && (
                <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                  <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                    <div className="flex w-full gap-4 items-center">
                      <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                        Personal Information
                      </span>
                      <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Name</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{clientManagers?.name ? toTitleCase(clientManagers.name, " ") : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Number</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{clientManagers?.mobile_number || "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Email ID</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-all">{clientManagers?.email_id || "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Joined Date</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{clientManagers?.created_at && !isNaN(new Date(clientManagers.created_at).getTime()) ? new Date(clientManagers.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Role</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">Business Admin</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {UserService.hasRole("agency_system_admin") && (
                <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                  <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                    <div className="flex w-full gap-4 items-center">
                      <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                        Personal Information
                      </span>
                      <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Name</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{clientAdmin?.name ? toTitleCase(clientAdmin.name, " ") : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Number</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{clientAdmin?.mobile_number || "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Email ID</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-all">{clientAdmin?.email_id || "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Joined Date</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{clientAdmin?.created_at && !isNaN(new Date(clientAdmin.created_at).getTime()) ? new Date(clientAdmin.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Role</p>
                        <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">System Admin</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {UserService.hasRole("agent") && (
                <>
                  {/* Personal Information */}
                  <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                    <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                      <div className="flex w-full gap-4 items-center">
                        <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                          Personal Information
                        </span>
                        <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Name</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{[agent?.first_name, agent?.last_name].filter(Boolean).join(" ") || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Number</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{agent?.mobile_number || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Email ID</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-all">{agent?.email_id || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Joined Date</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{agent?.created_at && !isNaN(new Date(agent.created_at).getTime()) ? new Date(agent.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Role</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">Agent</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Admin Assigned — the agent's system admin (ASA) */}
                  <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                    <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                      <div className="flex w-full gap-4 items-center">
                        <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                          Admin Assigned
                        </span>
                        <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                      </div>
                      {assignedAdmin ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                          <div>
                            <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Admin Name</p>
                            <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{assignedAdmin?.name || [assignedAdmin?.first_name, assignedAdmin?.last_name].filter(Boolean).join(" ") || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Admin Number</p>
                            <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{assignedAdmin?.mobile_number || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Admin Email ID</p>
                            <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-all">{assignedAdmin?.email_id || "—"}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-[#889DB2] italic">System admin not assigned yet</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {UserService.hasRole("customer") && (
                <>
                  {/* Personal Information */}
                  <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                    <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                      <div className="flex w-full gap-4 items-center">
                        <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                          Personal Information
                        </span>
                        <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Name</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{[customer?.first_name, customer?.last_name].filter(Boolean).join(" ") || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Number</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.mobile_number || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Email ID</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-all">{customer?.email_id || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Joined Date</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.created_at && !isNaN(new Date(customer.created_at).getTime()) ? new Date(customer.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Address</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">
                            {[customer?.address?.address_line1, customer?.address?.address_line2, customer?.address?.city, customer?.address?.province, customer?.address?.postal_code, customer?.address?.country].filter(Boolean).join(", ") || "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Agent Assigned */}
                  <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                    <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                      <div className="flex w-full gap-4 items-center">
                        <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                          Agent Assigned
                        </span>
                        <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                      </div>
                      {assignedAgent ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                          <div>
                            <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Agent Name</p>
                            <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{[assignedAgent?.first_name, assignedAgent?.last_name].filter(Boolean).join(" ") || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Agent Number</p>
                            <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{assignedAgent?.mobile_number || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Agent Email ID</p>
                            <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-all">{assignedAgent?.email_id || "—"}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-[#889DB2] italic">Agent not assigned yet</p>
                      )}
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                    <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                      <div className="flex w-full gap-4 items-center">
                        <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                          Additional Information
                        </span>
                        <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Investment Type</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.investment_type || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Annual Income</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.annual_income || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Budget</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.budget || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Occupation Type</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.occupation_type || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Income Source</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.income_source || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">Family Size</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{customer?.family_size ?? "—"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!UserService.hasRole("agent") && !UserService.hasRole("customer") && (
                <>
                  {/* Tax Details */}
                  <div className="bg-[#161F28] rounded-xl border border-[#374B5E] overflow-hidden">
                    <div className="flex flex-col gap-4 px-4 py-4 md:p-6">
                      <div className="flex w-full gap-4 items-center">
                        <span className="flex text-white text-sm sm:text-base md:text-lg font-medium rounded-xl py-1 whitespace-nowrap">
                          Tax Details
                        </span>
                        <hr className="flex-1 border-t border-[#FFFFFF] border-[0.3px] opacity-30" />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">GSTIN</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{agency?.gstn || "—"}</p>
                        </div>
                        <div>
                          <p className="text-[11px] sm:text-xs text-[#889DB2] font-medium uppercase tracking-wide">PAN</p>
                          <p className="text-sm md:text-base text-[#F4F5F7] mt-1 break-words">{agency?.pan || "—"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

function ClientCardSkeleton() {
  return (
    <div className="bg-[#161F28] rounded-lg border border-[#374B5E] overflow-hidden animate-pulse">
      <div className="px-3 py-3 md:p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-6">
          <div className="flex items-start gap-3">
            {/* Logo skeleton */}
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 rounded-lg bg-[#2E4051] shrink-0"></div>

            {/* Content skeleton */}
            <div className="flex flex-col justify-between md:h-24 gap-2">
              <div className="h-5 sm:h-6 bg-[#2E4051] rounded w-32 sm:w-48"></div>
              <div className="h-3 sm:h-4 bg-[#2E4051] rounded w-40 sm:w-64"></div>
              <div className="h-3 sm:h-4 bg-[#2E4051] rounded w-28 sm:w-40"></div>
            </div>
          </div>

          {/* Right side skeleton */}
          <div className="flex flex-col sm:items-end gap-1">
            <div className="h-3 sm:h-4 bg-[#2E4051] rounded w-28 sm:w-32 mb-1 sm:mb-2"></div>
            <div className="h-3 sm:h-4 bg-[#2E4051] rounded w-20 sm:w-24"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
